import { CrescLoader } from "../components/LoadingScreen";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { X } from "lucide-react";
import Logo from "../components/Logo";

const BRAND = "#5A90F5";
const inputCls = "w-full bg-[#141414] border border-[#2A2A2A] rounded-md px-4 py-3 text-[#EDEDED] focus:border-[#5A90F5] focus:shadow-[0_0_0_3px_rgba(90,144,245,0.15)] outline-none transition-all";

export default function Login() {
  const { loginWithCreds, accounts, removeAccount } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await loginWithCreds(email, password);
      toast.success("Welcome Back");
      navigate("/projects");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login Failed");
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = async () => {
    try {
      const redirectUri = window.location.origin + "/auth/callback";
      const { data } = await import("../lib/api").then(m => m.api.get(`/auth/google/url?redirect_uri=${encodeURIComponent(redirectUri)}`));
      window.location.href = data.url;
    } catch {
      toast.error("Google sign-in unavailable. Use email/password.");
    }
  };

  const recent = (accounts || []).slice(-3).reverse();

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] px-6">
      <div className="w-full max-w-md rounded-2xl border border-[#1F1F1F] bg-[#0F0F0F] p-8">
        <div className="flex items-center justify-center mb-5">
          <Logo size={32} />
        </div>
        <h1 className="font-display text-3xl font-semibold text-[#EDEDED] tracking-tight text-center">
          Sign In
        </h1>
        <p className="mt-1.5 text-xs italic tracking-wide text-center" style={{ color: BRAND }} data-testid="login-slogan">
          Productively, Creatively.
        </p>

        {recent.length > 0 && (
          <div className="mt-7" data-testid="remembered-accounts">
            <div className="meta-label mb-2">Continue As</div>
            <div className="space-y-1.5">
              {recent.map((a) => (
                <div key={a.user_id}
                  className="group w-full flex items-center gap-2.5 px-3 py-2 rounded-md bg-[#141414] border border-[#2A2A2A] hover:border-[#3A3A3A] transition-colors">
                  <button
                    type="button"
                    onClick={() => { setEmail(a.email || ""); setPassword(""); }}
                    className="flex-1 flex items-center gap-2.5 text-left"
                    data-testid={`remembered-account-${a.user_id}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-[#0A0A0A] border border-[#2A2A2A] flex items-center justify-center overflow-hidden flex-shrink-0">
                      {a.picture
                        ? <img src={a.picture} alt="" className="w-full h-full object-cover" />
                        : <span className="text-[11px] font-medium text-[#EDEDED]">{(a.username || a.name || a.email || "?")[0]?.toUpperCase()}</span>
                      }
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium text-[#EDEDED] truncate">{a.username || a.name || a.email}</div>
                      <div className="text-[10px] text-[#A1A1AA] truncate">{a.email}</div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeAccount?.(a.user_id)}
                    className="opacity-0 group-hover:opacity-100 p-1 rounded hover:bg-[#1a1a1a] text-[#A1A1AA] hover:text-[#FF3B30] transition-opacity"
                    title="Forget This Account"
                    data-testid={`forget-account-${a.user_id}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4 mt-6" data-testid="login-form">
          <div>
            <label className="meta-label block mb-2">EMAIL</label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputCls}
              placeholder="you@example.com"
              data-testid="login-email-input"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="meta-label block mb-2">PASSWORD</label>
            <input
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputCls}
              placeholder="Password"
              data-testid="login-password-input"
              autoComplete="current-password"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full font-medium py-3 rounded-md transition-all flex items-center justify-center gap-2 disabled:opacity-60 hover:shadow-[0_0_24px_0_rgba(90,144,245,0.45)] text-white"
            style={{ background: BRAND }}
            data-testid="login-form-submit-btn"
          >
            {loading && <CrescLoader size={16} />}
            Sign In
          </button>
        </form>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-[#2A2A2A]" />
          <span className="meta-label">OR</span>
          <div className="flex-1 h-px bg-[#2A2A2A]" />
        </div>

        <button
          onClick={googleLogin}
          className="w-full bg-[#141414] border border-[#2A2A2A] text-[#EDEDED] py-2.5 rounded-md hover:bg-[#1a1a1a] hover:border-[#3A3A3A] transition-all flex items-center justify-center gap-3 text-sm"
          data-testid="google-signin-btn"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </button>

        <p className="text-center text-xs text-[#A1A1AA] mt-6">
          No Account?{" "}
          <Link to="/register" className="text-[#EDEDED] underline underline-offset-2 hover:text-white" data-testid="to-register-link">
            Create One
          </Link>
        </p>
        <p className="text-center text-[10px] text-[#555] mt-2">
          <button
            type="button"
            onClick={() => { localStorage.clear(); toast.success("Session Cleared. Try Again."); setTimeout(() => window.location.reload(), 600); }}
            className="hover:text-[#A1A1AA]"
            data-testid="clear-session-link"
          >
            Trouble Signing In? Clear Saved Sessions
          </button>
        </p>
      </div>
    </div>
  );
}
